<template>
  <div
    ref="table"
    class="c-table"
    :class="{
      'c-table_scrolled': isScrolled,
      'c-table_scroll_end': isScrollEnd,
      'c-table_scroll': isScroll,
    }"
  >
    <m-column-filters
      ref="columnFilters"
      :column="filteredColumn"
      :reference-list="referenceList"
      :active-filters="activeFilteredColumns"
      @getReferenceValues="getReferenceValues"
      @close="filteredColumn = null"
      @setFilter="setColumnFilter"
      @removeFilter="removeColumnFilter"
    />

    <div
      v-if="displayShadow"
      :class="{
        'c-table__shadow': data.actions,
        'c-table__shadow_active': data.actions,
        // 'c-table__empty__shadow': data.isGroupByCategory && hideCollapseGroups.length === data.records.length
      }"
    ></div>
    <div v-if="isScroll" class="c-table__scrollbar-wrapper"></div>

    <perfect-scrollbar ref="wrapper" class="c-table__wrapper" @scroll="scroll">
      <table ref="container" class="c-table__container">
        <thead v-if="displayTableHead" ref="header" class="c-table__header">
          <tr v-for="i in levels" :key="i" class="c-table__header-row">
            <th
              v-for="column in getTableColumns(i)"
              :id="`c-table-column-${column.id}`"
              :key="column.id"
              class="c-table__column"
              :colspan="getColspan(column)"
              :rowspan="getRowspan(column)"
              :class="{
                'c-table__column_with_filter': column.type,
                'c-table__column_hidden': isDisabled(column.id),
                'c-table__column_sorted': isSorted(column),
              }"
            >
              <div class="c-table__column-wrapper">
                <div
                  class="c-table__column-label"
                  :class="{ 'c-table__column-label-children': !isCategory(column) && sortedColumn }"
                  @click="setSortedColumn(column, sortedColumn)"
                >
                  {{ column.label }}
                </div>

                <div v-if="column.description || column.type" class="c-table__column-actions">
                  <div v-if="column.fullName" class="c-table__column-action c-table__column-action_description">
                    <c-button :id="`table-column-button-description-${column.id}`" size="small" icon="info" no-border />

                    <b-popover
                      placement="top"
                      triggers="hover"
                      :target="`table-column-button-description-${column.id}`"
                    >
                      {{ column.fullName }}
                    </b-popover>
                  </div>

                  <div v-if="isColumnFiltered(column.id)" class="c-table__column-action">
                    <c-button
                      :id="`table-column-button-filter-${column.id}`"
                      size="small"
                      no-border
                      :active="isColumnActiveFiltered(column.id)"
                      :icon="{
                        icon: 'filter-column',
                        activeColor: 'blue',
                      }"
                      @click="openColumnFilterModal(column)"
                    />

                    <b-popover placement="top" triggers="hover" :target="`table-column-button-filter-${column.id}`">
                      {{ isColumnActiveFiltered(column.id) ? "Изменить фильтр" : "Применить фильтр" }}
                    </b-popover>
                  </div>
                </div>
              </div>
            </th>

            <th
              v-if="data.actions && i === 1"
              id="c-table-actions-element"
              class="c-table__column c-table__column_actions"
              :rowspan="levels"
            >
              <div class="c-table__column-empty"></div>
            </th>
          </tr>
        </thead>

        <tbody v-if="!data.isGroupByCategory" class="c-table__records">
          <tr
            v-for="(record, recordIndex) in sortedRecords"
            v-show="record.isShowRow"
            :key="recordIndex"
            class="c-table__record"
          >
            <td
              v-for="(column, cellIndex) in getTableColumns(levels)"
              :key="column.id"
              class="c-table__cell"
              :class="{
                'c-table__cell_string': column.type === 'string',
                'c-table__cell_reference': column.type === 'reference',
                'c-table__cell_number': column.type === 'number',
                'c-table__cell_archive': record.is_archive.value == 1,
                'c-table__cell_date': column.type === 'date',
                'c-table__cell_hidden': isDisabled(column.id),
                ['c-table__cell_' + column.id]: column.id,
              }"
            >
              <div v-if="column.id === 'name'" class="c-table__cell-value 1" style="display: flex; align-items: center;">

                <span>{{ record[column.id].value }}</span>
                <svg-icon v-if="record.is_vacant.value !== '0'" name="vacancy" v-b-popover.hover.blur.top="'Для должности есть вакансия'" style="display: inline-flex; margin-left: 10px;"></svg-icon>
              </div>
              <div v-else-if="isDefaultCell(record, column)" class="c-table__cell-value">{{ record[column.id].value }}</div>

              <c-table-cell
                v-else-if="isFilialSettingsCell(record, column)"
                class="c-table__cell-cell"
                :post-code="record.code.value"
                :cell-index="cellIndex"
                :cell-value="record[column.id].value"
                :column="column"
                :filial-settings="record.filialSettings.value"
              />
            </td>
            <td v-if="data.actions" class="c-table__cell c-table__cell_actions">
              <div class="c-table__cell-actions">
                <c-table-actions
                  :record="record"
                  :post-code="record.code.value"
                  :is-archive="record.is_archive.value"
                  :count-workers="getCountWorkers(record.code.value)"
                  @removePost="removePost"
                />
              </div>
            </td>
          </tr>
        </tbody>

        <tbody v-for="(group, groupIndex) in sortedRecords" :key="`body-${groupIndex}`" class="c-table__records">
          <template v-if="data.isGroupByCategory">
            <tr v-b-toggle="`category-collapse-${groupIndex}`" class="c-table__record">
              <td class="c-table__cell__collapse__button" :colspan="getTableColumns(levels).length + 1">
                <svg
                  class="c-table__cell__collapse__button__icon"
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="11" cy="11" r="10.5" fill="white" stroke="#D3D8DF" />
                  <path
                    d="M10.5398 13.4727C10.2813 13.2141 10.2813 12.7948 10.5398 12.5361L13.87 9.20431C14.1285 8.94569 14.5476 8.94569 14.8061 9.20432C15.0646 9.46294 15.0646 9.88225 14.8061 10.1409L11.4759 13.4727C11.2174 13.7313 10.7983 13.7313 10.5398 13.4727Z"
                    fill="#0066FF"
                  />
                  <path
                    d="M11.4706 13.4727C11.7291 13.2141 11.7291 12.7948 11.4706 12.5361L8.12999 9.19397C7.87149 8.93534 7.45238 8.93534 7.19388 9.19397C6.93537 9.45259 6.93537 9.8719 7.19388 10.1305L10.5345 13.4727C10.793 13.7313 11.2121 13.7313 11.4706 13.4727Z"
                    fill="#0066FF"
                  />
                </svg>
                {{ group.categoryName }}
                <div class="c-table__cell__collapse__button__border__right"></div>
              </td>
              <td v-if="data.actions" class="c-table__cell c-table__cell_actions">
                <div class="c-table__cell-actions c-table__cell-actions-collapse-button"></div>
              </td>
            </tr>
            <b-collapse
              v-for="(record, recordIndex) in group.records"
              v-show="record.isShowRow"
              :id="`category-collapse-${groupIndex}`"
              :key="recordIndex"
              tag="tr"
              class="c-table__record"
              visible
            >
              <td
                v-for="(column, cellIndex) in getTableColumns(levels)"
                :key="column.id"
                class="c-table__cell"
                :class="{
                  'c-table__cell_string': column.type === 'string',
                  'c-table__cell_reference': column.type === 'reference',
                  'c-table__cell_number': column.type === 'number',
                  'c-table__cell_archive': record.is_archive.value == 1,
                  'c-table__cell_date': column.type === 'date',
                  'c-table__cell_hidden': isDisabled(column.id),
                  ['c-table__cell_' + column.id]: column.id,
                }"
              >
                <div v-if="column.id === 'name'" class="c-table__cell-value" style="display: flex; align-items: center;">
                  <c-alarm
                    v-if="record.is_archive.value == 1 && getCountWorkers(record.code.value) > 0"
                    class="cell-filial-settings__icon"
                    :cell-index="cellIndex"
                    :post-code="record.code.value"
                    :count-workers="getCountWorkers(record.code.value)"
                  />
                  <span>{{ record[column.id].value }}</span>
                  <svg-icon v-if="record.is_vacant.value !== '0'" name="vacancy" v-b-popover.hover.blur.top="'Для должности есть вакансия'" style="display: inline-flex; margin-left: 10px;"></svg-icon>
                </div>

                <div v-else-if="isDefaultCell(record, column)" class="c-table__cell-value">
                  {{ record[column.id].value }}
                </div>

                <c-table-cell
                  v-else-if="isFilialSettingsCell(record, column)"
                  class="c-table__cell-cell"
                  :post-code="record.code.value"
                  :cell-index="cellIndex"
                  :cell-value="record[column.id].value"
                  :column="column"
                  :filial-settings="record.filialSettings.value"
                />
              </td>
              <td v-if="data.actions" class="c-table__cell c-table__cell_actions">
                <div class="c-table__cell-actions">
                  <c-table-actions
                    :record="record"
                    :post-code="record.code.value"
                    :is-archive="record.is_archive.value"
                    :count-workers="getCountWorkers(record.code.value)"
                    @removePost="removePost"
                  />
                </div>
              </td>
            </b-collapse>
          </template>
        </tbody>
      </table>
    </perfect-scrollbar>

    <div
      v-if="(data.activeFilteredColumns.length > 0 || data.globalFilters.length > 0) && data.records.length === 0"
      class="c-table__empty__records"
    >
      {{ emptyRecordsLabel }}
    </div>
    <div
      v-if="data.records.length === 0 && data.activeFilteredColumns.length === 0 && data.globalFilters.length === 0"
      class="c-table__empty__posts__not__added"
    >
      Должности для группы <b>{{ currentGroupName }}</b> пока не добавлены
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import Button from "@/components/controls/button";
import DataTableCell from "@/pages/positions/data-table/cell";
import DataTableActions from "@/pages/positions/data-table/actions";
import ColumnFiltersModal from "@/pages/positions/data-table/column-filters-positions";
import SvgIcon from "@/components/svg-icon";
import Alarm from "@/pages/positions/data-table/position-alarm-popover.vue";

export default {
  name: "c-table",

  components: {
    "c-alarm": Alarm,
    "c-button": Button,
    "c-table-cell": DataTableCell,
    "c-table-actions": DataTableActions,
    "m-column-filters": ColumnFiltersModal,
    SvgIcon
  },

  props: [
    "levels",
    "maxWidth",
    "data",
    "enabledFields",
    "disabledColumns",
    "sortedColumn",
    "filteredColumns",
    "activeFilteredColumns",
    "referenceList",
    "emptyRecordsLabel",
    "allGroups",
  ],

  data() {
    return {
      isScrolled: false,
      isScrollEnd: false,
      isScroll: false,
      positionTop: 0,
      filterFieldsBlockHeight: 0,
      filteredColumn: null,
      hideCollapseGroups: [],
    };
  },

  computed: {
    ...mapGetters("app", ["sidebarCollapsed"]),

    currentGroupName() {
      if (this.data.currentGroup && this.data.currentGroup.name) {
        return this.data.currentGroup.name;
      }

      return this.$route.params.code || "";
    },

    displayTableHead() {
      return (
        this.data.records.length > 0 ||
        (this.data.records.length === 0 && this.data.activeFilteredColumns.length > 0) ||
        (this.data.records.length === 0 && this.data.globalFilters.length > 0)
      );
    },

    displayShadow() {
      return (
        (!(this.data.isGroupByCategory && this.data.records.length !== 0) ||
          !(this.data.isGroupByCategory && this.hideCollapseGroups.length === this.data.records.length)) &&
        this.data.records.length > 0
      );
    },

    sortedRecords() {
      if (!this.sortedColumn) {
        return this.data.records;
      }

      const sortedColumn = this.sortedColumn.sortBy;
      const sortType = this.sortedColumn.sortType === "asc" ? 1 : -1;
      let sortedColumns;
      const indexCol = this.data.columns.findIndex((col) => col.id === sortedColumn);
      const typeCol = this.data.columns[indexCol] !== undefined ? this.data.columns[indexCol].type : undefined;

      if (this.data.isGroupByCategory) {
        sortedColumns = this.data.records.map((group) => ({
          categoryName: group.categoryName,
          records: group.records.sort((a, b) => {
            if (a[sortedColumn] === undefined && b[sortedColumn] === undefined) {
              return 0;
            }

            if (
              (a[sortedColumn].value === undefined || a[sortedColumn].value === null) &&
              (b[sortedColumn].value === undefined || b[sortedColumn].value === null)
            ) {
              return 0;
            }

            if (
              a[sortedColumn].value !== undefined &&
              a[sortedColumn].value !== null &&
              (b[sortedColumn].value === undefined || b[sortedColumn].value === null)
            ) {
              return 1 * sortType;
            }

            if (
              (a[sortedColumn].value === undefined || a[sortedColumn].value === null) &&
              b[sortedColumn].value !== undefined &&
              b[sortedColumn].value !== null
            ) {
              return -1 * sortType;
            }

            if (typeCol === "number") {
              return (a[sortedColumn].value - b[sortedColumn].value) * sortType;
            }

            if (a[sortedColumn].value.toLowerCase() < b[sortedColumn].value.toLowerCase()) {
              return -1 * sortType;
            }
            if (a[sortedColumn].value > b[sortedColumn].value) {
              return 1 * sortType;
            }
            return 0;
          }),
        }));
      } else {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        sortedColumns = this.data.records.sort((a, b) => {
          if (a[sortedColumn] === undefined && b[sortedColumn] === undefined) {
            return 0;
          }

          if (
            (a[sortedColumn].value === undefined || a[sortedColumn].value === null) &&
            (b[sortedColumn].value === undefined || b[sortedColumn].value === null)
          ) {
            return 0;
          }

          if (
            a[sortedColumn].value !== undefined &&
            a[sortedColumn].value !== null &&
            (b[sortedColumn].value === undefined || b[sortedColumn].value === null)
          ) {
            return 1 * sortType;
          }

          if (
            (a[sortedColumn].value === undefined || a[sortedColumn].value === null) &&
            b[sortedColumn].value !== undefined &&
            b[sortedColumn].value !== null
          ) {
            return -1 * sortType;
          }

          if (typeCol === "number") {
            return (a[sortedColumn].value - b[sortedColumn].value) * sortType;
          }

          if (a[sortedColumn].value.toLowerCase() < b[sortedColumn].value.toLowerCase()) {
            return -1 * sortType;
          }
          if (a[sortedColumn].value > b[sortedColumn].value) {
            return 1 * sortType;
          }
          return 0;
        });
      }

      return sortedColumns;
    },
  },

  watch: {
    "enabledFields": function () {
      this.updatePositionTop();
    },

    "isScrollEnd": function () {
      // console.log('value: ', value)
    },

    "data.columns": function () {
      // console.log('value: ', value)
    },

    "disabledColumns": function (value) {
      const enabledColumns = this.data.columns.filter((item) => !value.includes(item.id));

      const max = Math.max.apply(
        null,
        enabledColumns.map((item) => item.level),
      );

      this.levels = max + 1;

      this.setTableShadow();
    },
  },

  mounted() {
    this.updatePositionTop();

    window.addEventListener("touchend", this.handleTouchMove, {
      passive: false,
    });
    const layoutElement = document.querySelector(".layout");
    if (layoutElement !== null) {
      layoutElement.addEventListener("scroll", this.handleLayoutScroll);
    }

    const perfectScrollbarElement = document.querySelector(".ps");
    if (perfectScrollbarElement !== null) {
      perfectScrollbarElement.addEventListener("scroll", this.handleTableScroll);
    }

    this.isScroll = this.$refs.wrapper.$el.clientWidth < this.$refs.wrapper.$el.scrollWidth;
    setTimeout(() => {
      this.setTableShadow();
    }, 1500);

    this.$root.$on("bv::collapse::state", (collapseId, isJustShown) => {
      if (!isJustShown) {
        if (!this.hideCollapseGroups.includes(collapseId)) this.hideCollapseGroups.push(collapseId);
      } else {
        const c = this.hideCollapseGroups.indexOf(collapseId);
        if (c !== -1) this.hideCollapseGroups.splice(c, 1);
        this.setTableShadow();
      }
    });
  },

  methods: {
    removePost() {
      this.$emit("removePost");
    },

    handleTableScroll() {
      this.setTableShadow();
    },

    handleTouchMove() {
      // if (!e.cancelable) {
      //   e.preventDefault();
      //   return true
      // }
    },

    setTableShadow() {
      if (this.data.actions === undefined) {
        return;
      }
      const ps = document.querySelector(".ps");
      // const tableWrapper = document.querySelector(".c-table__wrapper");
      const tableWrapperShadow = document.querySelector(".c-table__shadow");
      const actionsBlock = document.getElementById("c-table-actions-element");

      if (!ps || !tableWrapperShadow) return;

      const fullScrollWidth = ps.scrollWidth - ps.clientWidth;

      const diff = Math.abs(ps.scrollLeft - fullScrollWidth);

      if (diff > 0 && this.data.records.length !== 0) {
        tableWrapperShadow.classList.add("c-table__shadow_active");
        actionsBlock.classList.add("c-table__column_actions_shadow");
      } else {
        tableWrapperShadow.classList.remove("c-table__shadow_active");
        actionsBlock.classList.remove("c-table__column_actions_shadow");
      }
    },

    resetColumnFilters() {
      this.activeFilteredColumns = [];

      this.$refs.columnFilters.resetFilters();
    },

    isColumnFiltered(columnId) {
      if (this.filteredColumns === undefined) {
        return false;
      }
      const result = this.filteredColumns.findIndex((i) => i === columnId);

      return result !== -1;
    },

    isColumnActiveFiltered(columnId) {
      const c = this.activeFilteredColumns.findIndex((i) => i.id === columnId);

      return c !== -1;
    },

    setColumnFilter(e) {
      const c = this.activeFilteredColumns.findIndex((i) => i.id === e.id);

      const filterOptions = {
        id: e.id,
        type: e.type,
        searchType: e.filter.condition.value,
        value: e.type === "number" && e.filter.condition.value === "4" ? e.filter.rangeValue : e.filter.value,
      };

      if (
        c === -1 &&
        (filterOptions.type === "string" || filterOptions.type === "number") &&
        filterOptions.value === ""
      ) {
        return;
      }

      if (
        c !== -1 &&
        (filterOptions.type === "string" || filterOptions.type === "number") &&
        filterOptions.value === ""
      ) {
        this.activeFilteredColumns.splice(c, 1);
        return;
      }

      if (
        c === -1 &&
        filterOptions.type === "number" &&
        filterOptions.searchType === "4" &&
        (filterOptions.value.end === "" || filterOptions.value.start === "")
      ) {
        return;
      }

      if (
        c !== -1 &&
        filterOptions.type === "number" &&
        filterOptions.searchType === "4" &&
        (filterOptions.value.end === "" || filterOptions.value.start === "")
      ) {
        this.activeFilteredColumns.splice(c, 1);
        return;
      }

      if (c === -1 && filterOptions.type === "list" && filterOptions.value.length === 0) {
        return;
      }

      if (c !== -1 && filterOptions.value.length === 0) {
        this.activeFilteredColumns.splice(c, 1);
        return;
      }

      if (c === -1) {
        this.activeFilteredColumns.push(filterOptions);
      } else {
        this.activeFilteredColumns.splice(c, 1);
        this.activeFilteredColumns[c] = filterOptions;
      }
    },

    isCategory(column) {
      return column.isCategory === 1;
    },

    isSorted(column) {
      return column.isSorted;
    },

    setSortedColumn(column, sortedColumn) {
      if (column.isCategory !== 1 && sortedColumn !== undefined) {
        const c = this.data.columns.findIndex((i) => i.id === sortedColumn.sortBy);

        if (c !== -1) {
          this.data.columns[c].isSorted = false;
        }

        column.isSorted = true;
        this.sortedColumn.sortBy = column.id;

        if (column.id === sortedColumn.sortBy) {
          this.sortedColumn.sortType = this.sortedColumn.sortType === "asc" ? "desc" : "asc";
        } else {
          this.sortedColumn.sortType = "asc";
        }

        this.$emit("sortedEvent", this.sortedColumn);
      }
    },

    removeColumnFilter(columnId) {
      const c = this.activeFilteredColumns.findIndex((i) => i.id === columnId);

      if (c !== -1) {
        this.activeFilteredColumns.splice(c, 1);
      }
    },

    openColumnFilterModal(column) {
      const c = this.filteredColumns.findIndex((i) => i === column.id);

      if (c !== -1) {
        this.filteredColumn = column;
      }
    },

    handleLayoutScroll() {
      const layout = document.querySelector(".layout");
      const app = document.querySelector(".app");

      let result = layout.scrollTop - this.positionTop;

      if (app.offsetWidth < 992) {
        result += 50;
      }

      if (layout.scrollTop > this.positionTop) {
        if (this.$refs.header) {
          this.$refs.header.classList.add("c-table__header-shadow");
          this.$refs.header.style.transform = `translateY(${result}px)`;
        }
      } else if (this.$refs.header) {
        this.$refs.header.classList.remove("c-table__header-shadow");
        this.$refs.header.style.transform = "none";
      }
    },

    updatePositionTop() {
      if (this.$refs.header !== undefined) {
        const header = this.$refs.header.getBoundingClientRect();
        const layout = document.querySelector(".layout");
        this.positionTop = header.top + layout.scrollTop;
      }
    },

    isDefaultCell(record, column) {
      return !this.disabledColumns.includes(column.id) && record.filialSettings.value.length === 0;
    },

    isFilialSettingsCell(record, column) {
      return !this.disabledColumns.includes(column.id) && record.filialSettings.value.length > 0;
    },

    getChildrens(columnId) {
      return this.data.columns.filter((item) => item.parentColumnId === columnId);
    },

    getTableColumns(level) {
      let out = [];

      const columns = this.data.columns.filter((item) => item.parentColumnId === undefined);

      if (level === 1) {
        out = out.concat(columns);
      } else if (level === 2) {
        Object.keys(columns).forEach((i) => {
          out = out.concat(this.getChildrens(columns[i].id));
        });
      } else if (level === 3) {
        Object.keys(columns).forEach((i) => {
          const childrens = this.getChildrens(columns[i].id);

          Object.keys(childrens).forEach((ii) => {
            out = out.concat(this.getChildrens(childrens[ii].id));
          });
        });
      }

      return out;
    },

    getColumns(level) {
      let cols = [];

      if (level === 1) {
        cols = this.data.columns.filter((col) => col.parentColumnId === undefined);
      } else if (level === 2) {
        const rootCols = this.data.columns.filter((col) => col.parentColumnId === undefined);

        Object.keys(rootCols).forEach((i) => {
          const rootCol = rootCols[i];

          const childrens = this.data.columns.filter((col) => col.parentColumnId === rootCol.id);

          cols = cols.concat(childrens);
        });
      } else if (level === 3) {
        const rootCols = this.data.columns.filter((col) => col.parentColumnId === undefined);

        Object.keys(rootCols).forEach((i) => {
          const rootCol = rootCols[i];

          const childrens = this.data.columns.filter((col) => col.parentColumnId === rootCol.id);

          Object.keys(childrens).forEach((ii) => {
            const childrens2 = this.data.columns.filter((col) => col.parentColumnId === childrens[ii].id);
            cols = cols.concat(childrens2);
          });
        });
      }

      return cols;
    },

    getColspan(column) {
      const children = this.data.columns.filter((col) => col.parentColumnId === column.id);

      let count = children.length;

      if (column.level === 0) {
        Object.keys(children).forEach((c) => {
          const childrenChildren = this.data.columns.filter((col) => col.parentColumnId === children[c].id);

          count += childrenChildren.length;
        });
      }

      Object.keys(children).forEach((i) => {
        const result = this.disabledColumns.find((item) => item === children[i].id);

        if (result) {
          count -= 1;
        }
      });

      return count;
    },

    getRowspan(column) {
      const children = this.data.columns.filter((col) => col.parentColumnId === column.id);

      if (this.levels === 3) {
        if (column.level === 0 && children.length === 0) {
          return 3;
        }
        if (column.level === 1 && children.length === 0) {
          return 2;
        }
      } else if (this.levels === 2) {
        if (column.level === 0 && children.length === 0) {
          return 2;
        }
      }

      return 1;
    },

    getSeletItemLabel(record, column) {
      let cell = null;

      if (column.items) {
        const item = column.items.find((i) => i.value === record[column.name]);

        cell = item ? item.label : "-";
      } else {
        cell = "-";
      }

      return cell;
    },

    scroll() {
      // const layout = document.querySelector(".layout");
      // layout.classList.add('layout-touch-action-none');
      this.isScrolled = this.$refs.wrapper.$el.scrollLeft > 0;
      const containerWidth = this.$refs.container.offsetWidth;
      const wrapperWidth = this.$refs.wrapper.$el.offsetWidth;
      const { scrollLeft } = this.$refs.wrapper.$el;
      this.isScrollEnd = containerWidth - scrollLeft === wrapperWidth - 1;
      // layout.classList.remove('layout-touch-action-none');
    },

    isDisabled(id) {
      return this.disabledColumns.find((item) => item === id);
    },

    getReferenceValues(e) {
      this.$emit("getReferenceValues", e);
    },

    getCountWorkers(postCode) {
      let countWorkers;
      this.allGroups.forEach((group) => {
        group.categories.forEach((category) => {
          const c = category.posts.findIndex((post) => post.code === postCode);
          if (c !== -1) {
            countWorkers = category.posts[c].countWorkers;
          }
        });
      });
      return countWorkers;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.c-table {
  position: relative;
  display: inline-block;
  max-width: 100%;

  &__record {
    &:focus {
      outline: none;
    }
  }

  &__scrollbar-wrapper {
    position: fixed;
    height: 24px;
    background: white;
    box-shadow: $box-shadow;
    bottom: 0;
    left: 0;
    z-index: 100;
    transition: 0.2s;

    @media screen and (max-width: 992px) {
      display: none;
    }
  }

  @media screen and (max-width: 992px) {
    max-width: 100%;
  }

  @media screen and (max-width: 700px) {
    margin: 0 -20px;
    width: calc(100% + 40px);
    max-width: none;
  }

  &__wrapper {
    overflow-x: auto;
    position: relative;
    transition: 0.2s;
    display: inline-block;
    width: 100%;

    &::-webkit-scrollbar {
      height: 0;
    }

    @media screen and (max-width: 992px) {
      width: 100%;
    }

    ::v-deep & .ps__rail-y {
      display: none !important;
    }
  }

  &__shadow {
    position: absolute;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #001550 100%);
    width: 10px;
    height: calc(100% - 19px);
    right: 113px;
    opacity: 0;
    bottom: 10px;
    visibility: hidden;
    z-index: 80;
    transition: 0.2s;
  }

  &__empty__shadow {
    right: 0;
  }

  &__empty__records {
    border: 1px solid $color-border-alt;
    width: 100%;
    height: 200px;
    display: flex;
    padding: 10px;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    z-index: $z-index-loader;
    background: white;
    color: $label;
    font-size: 15px;
  }

  &__empty__posts__not__added {
    font-size: 15px;

    @media screen and (max-width: 700px) {
      margin: 0 20px;
    }
  }

  &__shadow_active {
    opacity: 0.15;
    visibility: visible;
  }

  &_scroll {
    .c-table__column_actions {
      &:after {
        visibility: visible !important;
        opacity: 0.15 !important;
      }
    }

    &.c-table_scroll_end {
      .c-table__shadow {
        visibility: hidden;
        opacity: 0;
      }

      .c-table__column_actions {
        &:after {
          visibility: hidden !important;
          opacity: 0 !important;
        }
      }
    }
  }

  &_scrolled {
    border-left: 1px solid $color-border-light;

    @media screen and (max-width: 992px) {
      border-left: none;

      &:before {
        position: absolute;
        content: "";
        width: 22px;
        height: 100%;
        background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
        top: 0;
        left: 0;
        z-index: 100;
      }
    }
  }

  .c-table {
    &__container {
      border-collapse: collapse;
      width: calc(100% - 1px);
    }

    &__header {
      border-bottom: 2px solid $color-border-light;
      position: relative;
      background: $color-border-light;
      z-index: 82;
    }

    &__header-shadow:after {
      content: "";
      position: absolute;
      background: linear-gradient(#001550, rgba(255, 255, 255, 0));
      width: 100%;
      height: 10px;
      opacity: 0.15;
      z-index: 69;
      bottom: -10px;
      left: 0;
    }

    &__column {
      text-align: left;
      border: 1px solid $color-border-light;
      vertical-align: top;
      height: 1px;
      padding: 0;
      transition: 0.2s;

      &_hidden {
        width: 0;
        border: none;
        overflow: hidden;
        display: none;
        // margin-left: -1px;

        .c-table__column-wrapper {
          width: 0;
          padding: 0;
          border: none;
        }
      }

      &_with {
        &_filter {
          .c-table__column {
            &-wrapper {
              flex-direction: column;
            }

            &-actions {
              margin-top: 10px;
            }
          }
        }
      }

      &_sorted {
        .c-table__column-label {
          color: $color-primary;
        }
      }

      &-label {
        color: $color-label;
        font: {
          size: 12px;
          weight: 500;
        }
        line-height: 13px;

        &-children {
          cursor: pointer;
        }
      }

      &-wrapper {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding: 10px;
        height: 100%;
        background: white;
      }

      &-actions {
        display: flex;
        justify-content: space-between;
      }

      &-action {
        width: 14px;
        height: 14px;

        .c-button {
          width: 14px;
          height: 14px;
        }

        &_description {
          margin-left: -3px;
        }
      }

      &_actions {
        position: sticky;
        right: 0;
        border-right: none;
        z-index: 30;

        &_shadow {
          &:after {
            content: "";
            position: absolute;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #001550 100%);
            width: 9px;
            height: 100%;
            left: -8px;
            opacity: 0;
            bottom: 0;
            visibility: hidden;
            z-index: 40;
          }
        }
      }

      &-empty {
        background: white;
        width: calc(100% + 1px);
        height: calc(100% + 2px);
        border-right: 1px solid $color-border-light;
        border-left: 1px solid $color-border-light;
        border-bottom: 2px solid $color-border-light;
        margin-left: -1px;
        position: relative;
      }
    }

    &__cell {
      border: 1px solid $color-border-light;
      padding: 0;
      height: 1px;
      //transition: .2s;

      &_minShiftDuration {
        text-align: right;
      }

      &_loadType {
        text-align: right;
      }

      &__collapse__button {
        height: 38px;
        vertical-align: middle;
        padding: 0 10px;
        white-space: nowrap;
        background: #f5f7fa;
        overflow: hidden;
        position: relative;
        border: 1px solid #dfe4ef;
        border-right: none;
        z-index: 81;
        font-family: Fira Sans;
        font-weight: bold;
        font-size: 13px;
        color: #000000;

        &__icon {
          margin-right: 10px;
        }

        &__border__right {
          position: absolute;
          top: 0;
          right: 0;
          height: 100%;
          width: 1px;
          background: #dfe4ef;
        }
      }

      &_number {
        & .c-table__cell-value {
          text-align: right;
        }
      }

      &_archive {
        & .c-table__cell-value, & .c-table__cell-cell {
          color: #00000070;
        }
      }

      &_rank {
        & .c-table__cell-value {
          text-align: left;
        }
      }

      &_hidden {
        width: 0;
        overflow: hidden;
        border: none;
        display: none;

        .c-table__cell-value,
        .c-table__cell-cell {
          width: 0;
          padding: 0;
        }
      }

      &-cell {
        display: flex;
        align-items: center;
        transition: 0.2s;
      }

      &-value {
        line-height: 13px;
        font-size: 13px;
        padding: 10px !important;
        transition: font 0.3s ease;
      }

      &-pre-formatted {
        white-space: pre;
      }

      &_actions {
        position: sticky;
        right: 0;
        border-right: none;
        z-index: 20;
      }

      &-actions {
        background: white;
        width: calc(100% + 1px);
        min-height: 30px;
        height: 100%;
        display: flex;
        align-items: center;
        border-right: 1px solid $color-border-light;
        border-left: 1px solid $color-border-light;
        margin-left: -1px;

        &-collapse-button {
          border-left: 0;
        }
      }
    }
  }
}

::v-deep .collapsing {
  transition: font 0.5s ease;
}

::v-deep .collapse {
  display: table-row !important;
}

::v-deep .collapsed ~ .collapse {
  max-height: 0 !important;

  & .c-table__cell {
    border: 0 !important;
    height: 0 !important;
    max-height: 0 !important;
  }

  & .c-table__cell .c-table__cell-value {
    color: #ffffff !important;
    line-height: 0 !important;
    padding: 0 !important;
    font-size: 0;
    transition: font 0.3s ease;
  }

  & .c-table__cell .cell-filial-settings {
    visibility: hidden !important;
    height: 0 !important;
    max-height: 0 !important;
    padding: 0 !important;
    font-size: 0 !important;

    & .cell-filial-settings__value {
      font-size: 0;
      line-height: 0;
    }

    & .cell-filial-settings__icon {
      display: none;
    }
  }

  & .c-table__cell_actions .c-table__cell-actions {
    display: none;
  }
}
</style>
